import { z } from 'zod';
import { commissionDealsSchema } from '../pricing/commission-rates';
import { importConflictsSchema, importStatusSchema } from './import-status';

// Verbatim from legacy `travel_planner_back/src/crm/enums/contact-salutation.ts`.
export const contactSalutationValues = [
  'Mr.',
  'Mrs.',
  'Miss',
  'Dr.',
  'Prof.',
] as const;
export const contactSalutationSchema = z.enum(contactSalutationValues);
export type ContactSalutation = z.infer<typeof contactSalutationSchema>;

// Verbatim from legacy `travel_planner_back/src/crm/enums/company-type.ts`.
export const companyTypeValues = [
  'Agent',
  'Customer',
  'Lead',
  'Prospect',
  'Supplier',
] as const;
export const companyTypeSchema = z.enum(companyTypeValues);
export type CompanyType = z.infer<typeof companyTypeSchema>;

/** The supplier account type — the one place the literal is written. */
export const SUPPLIER_COMPANY_TYPE = 'Supplier' as const satisfies CompanyType;

// XT-892: Agent, Lead and Prospect are retired — nothing new is created with
// them (Agent returns after the CRM import). They stay in `companyTypeValues`
// so stored rows still parse.
export const retiredCompanyTypeValues = [
  'Agent',
  'Lead',
  'Prospect',
] as const satisfies readonly CompanyType[];
export const selectableCompanyTypeValues = companyTypeValues.filter(
  (value): value is Exclude<CompanyType, RetiredCompanyType> =>
    !(retiredCompanyTypeValues as readonly string[]).includes(value)
);
export type RetiredCompanyType = (typeof retiredCompanyTypeValues)[number];
export const isRetiredCompanyType = (value: string | null | undefined) =>
  (retiredCompanyTypeValues as readonly string[]).includes(value ?? '');

export const consortiumValues = [
  'Virtuoso',
  'SmartFlyer',
  'Traveller Made',
  'Signature',
  'Ensemble',
  'Other',
] as const;
export const consortiumSchema = z.enum(consortiumValues);
export type Consortium = z.infer<typeof consortiumSchema>;

export const lifecycleStageValues = [
  'Lead',
  'Marketing Qualified',
  'Sales Qualified',
  'Opportunity',
  'Customer',
  'Other',
] as const;
export const lifecycleStageSchema = z.enum(lifecycleStageValues);
export type LifecycleStage = z.infer<typeof lifecycleStageSchema>;

export const preferredHotelTypeValues = [
  'Boutique',
  '5★',
  'Resort',
  'Villa',
  'Palace',
  'Other',
] as const;
export const preferredHotelTypeSchema = z.enum(preferredHotelTypeValues);
export type PreferredHotelType = z.infer<typeof preferredHotelTypeSchema>;

export const specialOccasionValues = [
  'Honeymoon',
  'Anniversary',
  'Birthday',
  'Wedding',
  'Business',
  'Other',
] as const;
export const specialOccasionSchema = z.enum(specialOccasionValues);
export type SpecialOccasion = z.infer<typeof specialOccasionSchema>;

// Controlled role list for a supplier's contacts
// (apps/crm/prd/crm-supplier-record.md §5). Stored in `contact.title` — there
// is no separate role column. Likewise there is no `is_active` column: a
// contact is active iff `deletedOn IS NULL`. Do NOT repurpose `agentStatus`,
// which carries HubSpot `hs_lead_status` values.
export const contactRoleValues = [
  'Sales',
  'Director',
  'Operations',
  'Reservations',
  'Finance',
  'Contracting',
  'Emergency',
  'Other',
] as const;
export const contactRoleSchema = z.enum(contactRoleValues);
export type ContactRole = z.infer<typeof contactRoleSchema>;

/**
 * XT-903 — `contact_origin` a contact created by its own portal sign-up (on
 * first magic-link login) is given, for display. The AI-assistant rule reads
 * `selfRegistered`, which no form or import can rewrite.
 */
export const SELF_REGISTERED_CONTACT_ORIGIN = 'agent_magic_link';

export const contactSchema = z.object({
  id: z.uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  companyId: z.uuid().nullable(),
  company: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      companyType: z.string().nullable(),
      companyStatus: z.string().nullable(),
    })
    .nullable(),
  title: z.string().nullable(),
  officePhone: z.string().nullable(),
  mobilePhone: z.string().nullable(),
  country: z.string().nullable(),
  city: z.string().nullable(),
  streetAddress: z.string().nullable(),
  postalCode: z.string().nullable(),
  stateOrRegion: z.string().nullable(),
  weChatId: z.string().nullable(),
  whatsApp: z.string().nullable(),
  skypeId: z.string().nullable(),
  linkedin: z.string().nullable(),
  facebook: z.string().nullable(),
  instagram: z.string().nullable(),
  agentStatus: z.string().nullable(),
  preferredLanguage: z.string().nullable(),
  salutation: z.string().nullable(),
  notes: z.string().nullable(),
  leadSource: z.array(z.string()).nullable(),
  label: z.array(z.string()).nullable(),
  industry: z.string().nullable(),
  contactOrigin: z.string().nullable(),
  externalId: z.string().nullable(),
  ownerId: z.uuid().nullable(),
  consortium: z.string().nullable(),
  lifecycleStage: z.string().nullable(),
  preferredHotelType: z.string().nullable(),
  regionsOfInterest: z.array(z.string()).nullable(),
  estimatedBudget: z.string().nullable(),
  numberOfDays: z.number().int().nullable(),
  numberOfPassengers: z.number().int().nullable(),
  arrivalDate: z.string().nullable(),
  departureDate: z.string().nullable(),
  foodRestrictions: z.string().nullable(),
  specialOccasion: z.string().nullable(),
  travellerInterests: z.array(z.string()).nullable(),
  tripBriefing: z.string().nullable(),
  // XT-903 — approval a self-registered contact needs before it may connect an
  // AI assistant (MCP); contacts the DMC registered connect without it.
  aiAccess: z.boolean(),
  // XT-903 — a consultant blocked this contact from connecting an AI assistant.
  aiBlocked: z.boolean(),
  // XT-368 — a consultant blocked this contact from the agent portal.
  portalBlocked: z.boolean(),
  // XT-964 — the agent's own commission deal, a rate per category.
  commissionDeals: commissionDealsSchema,
  // XT-903 — created by its own portal sign-up; read-only (no input sets it).
  selfRegistered: z.boolean(),
  // XT-903 — signed in as by several people (the portal's shared demo login);
  // read-only, set by the portal when it issues a demo link.
  sharedLogin: z.boolean(),
  // The contact's own preview logo (uploadthing key); see `contacts.setLogo`.
  logoKey: z.string().nullable().optional(),
  importStatus: importStatusSchema.nullable(),
  importConflicts: importConflictsSchema,
  organizationId: z.uuid(),
  createdOn: z.string().nullable(),
  modifiedOn: z.string().nullable(),
  deletedOn: z.string().nullable(),
});

export type Contact = z.infer<typeof contactSchema>;

export const createContactInput = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  companyId: z.uuid().nullable().optional(),
  title: z.string().optional(),
  officePhone: z.string().optional(),
  mobilePhone: z.string().optional(),
  country: z.string().optional(),
  city: z.string().optional(),
  streetAddress: z.string().optional(),
  postalCode: z.string().optional(),
  stateOrRegion: z.string().optional(),
  weChatId: z.string().optional(),
  whatsApp: z.string().optional(),
  skypeId: z.string().optional(),
  linkedin: z.string().optional(),
  facebook: z.string().optional(),
  instagram: z.string().optional(),
  agentStatus: z.string().optional(),
  preferredLanguage: z.string().optional(),
  salutation: z.string().optional(),
  notes: z.string().optional(),
  leadSource: z.array(z.string()).optional(),
  label: z.array(z.string()).optional(),
  industry: z.string().optional(),
  contactOrigin: z.string().optional(),
  externalId: z.string().optional(),
  ownerId: z.uuid().nullable().optional(),
  consortium: z.string().optional(),
  lifecycleStage: z.string().optional(),
  preferredHotelType: z.string().optional(),
  regionsOfInterest: z.array(z.string()).optional(),
  estimatedBudget: z.string().optional(),
  numberOfDays: z.number().int().optional(),
  numberOfPassengers: z.number().int().optional(),
  arrivalDate: z.string().optional(),
  departureDate: z.string().optional(),
  foodRestrictions: z.string().optional(),
  specialOccasion: z.string().optional(),
  travellerInterests: z.array(z.string()).optional(),
  tripBriefing: z.string().optional(),
  aiAccess: z.boolean().optional(),
  aiBlocked: z.boolean().optional(),
  portalBlocked: z.boolean().optional(),
  commissionDeals: commissionDealsSchema.optional(),
});

export type CreateContactInput = z.infer<typeof createContactInput>;

export const updateContactInput = z.object({
  id: z.uuid(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  companyId: z.uuid().nullable().optional(),
  title: z.string().optional(),
  officePhone: z.string().optional(),
  mobilePhone: z.string().optional(),
  country: z.string().optional(),
  city: z.string().optional(),
  streetAddress: z.string().optional(),
  postalCode: z.string().optional(),
  stateOrRegion: z.string().optional(),
  weChatId: z.string().optional(),
  whatsApp: z.string().optional(),
  skypeId: z.string().optional(),
  linkedin: z.string().optional(),
  facebook: z.string().optional(),
  instagram: z.string().optional(),
  agentStatus: z.string().optional(),
  preferredLanguage: z.string().optional(),
  salutation: z.string().optional(),
  notes: z.string().optional(),
  leadSource: z.array(z.string()).optional(),
  label: z.array(z.string()).optional(),
  industry: z.string().optional(),
  contactOrigin: z.string().optional(),
  externalId: z.string().optional(),
  ownerId: z.uuid().nullable().optional(),
  consortium: z.string().optional(),
  lifecycleStage: z.string().optional(),
  preferredHotelType: z.string().optional(),
  regionsOfInterest: z.array(z.string()).optional(),
  estimatedBudget: z.string().optional(),
  numberOfDays: z.number().int().optional(),
  numberOfPassengers: z.number().int().optional(),
  arrivalDate: z.string().optional(),
  departureDate: z.string().optional(),
  foodRestrictions: z.string().optional(),
  specialOccasion: z.string().optional(),
  travellerInterests: z.array(z.string()).optional(),
  tripBriefing: z.string().optional(),
  aiAccess: z.boolean().optional(),
  aiBlocked: z.boolean().optional(),
  portalBlocked: z.boolean().optional(),
  commissionDeals: commissionDealsSchema.optional(),
});

export type UpdateContactInput = z.infer<typeof updateContactInput>;

export const contactSortFields = [
  'name',
  'email',
  'phone',
  'account',
  'country',
  'status',
] as const;

export const findContactsInput = z.object({
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(50),
  companyName: z.string().optional(),
  companyType: z.array(z.string()).optional(),
  contactName: z.string().optional(),
  // Supplier record's Contacts tab: every contact under one company. Distinct
  // from `companyName`, which is a substring search on the joined name.
  companyId: z.uuid().optional(),
  organizationIds: z.array(z.uuid()).optional(),
  sortBy: z.enum(contactSortFields).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

export type FindContactsInput = z.infer<typeof findContactsInput>;

export const paginatedContactsSchema = z.object({
  data: z.array(contactSchema),
  total: z.number(),
  offset: z.number(),
  limit: z.number(),
});

export type PaginatedContacts = z.infer<typeof paginatedContactsSchema>;

export const CONTACT_EXCEL_IMPORT_ROW_CAP = 5000;

export const contactExcelImportInput = z.object({
  base64: z.string().max(18_000_000),
  mode: z.enum(['create', 'upsert']).default('create'),
});

export type ContactExcelImportInput = z.infer<typeof contactExcelImportInput>;

export const contactExcelImportRowErrorSchema = z.object({
  row: z.number(),
  column: z.number().optional(),
  field: z.string().optional(),
  message: z.string(),
  value: z.unknown().optional(),
});

export type ContactExcelImportRowError = z.infer<
  typeof contactExcelImportRowErrorSchema
>;

export const contactExcelImportResultSchema = z.object({
  inserted: z.number(),
  skipped: z.number(),
  errors: z.array(contactExcelImportRowErrorSchema),
});

export type ContactExcelImportResult = z.infer<
  typeof contactExcelImportResultSchema
>;
